import React, { useState } from 'react';
import { FadeIn } from './FadeIn';
import { ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface ModuleProps {
  number: string;
  emoji: string;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
}
function ModuleCard({
  number,
  emoji,
  title,
  subtitle,
  children,
  isOpen,
  onToggle
}: ModuleProps) {
  return <div className="bg-white rounded-2xl shadow-sm border-l-4 border-l-green-lime overflow-hidden mb-4 transition-all hover:shadow-md" data-id="element-196">
      <button onClick={onToggle} className="w-full text-left p-5 md:p-6 flex items-start gap-4 focus:outline-none" data-id="element-197">
        <div className="shrink-0 flex flex-col items-center gap-2" data-id="element-198">
          <div className="w-8 h-8 rounded-full bg-green-mint/30 text-green-deep flex items-center justify-center font-bold text-sm" data-id="element-199">
            {number}
          </div>
          <span className="text-2xl" data-id="element-200">{emoji}</span>
        </div>

        <div className="flex-1 pr-4" data-id="element-201">
          <h3 className="font-bold text-lg md:text-xl text-green-deep mb-1" data-id="element-202">
            {title}
          </h3>
          {subtitle && <p className="text-sm text-text-main/70 italic" data-id="element-203">{subtitle}</p>}
        </div>

        <div className={`shrink-0 mt-2 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} data-id="element-204">
          <ChevronDown className="w-5 h-5 text-green-deep/50" data-id="element-205" />
        </div>
      </button>

      <AnimatePresence data-id="element-206">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3
      }} data-id="element-207">
            <div className="p-5 md:p-6 pt-0 pl-[4.5rem] text-text-main/80 text-sm md:text-base leading-relaxed border-t border-gray-50" data-id="element-208">
              {children}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
export function ModulesSection() {
  const [openModule, setOpenModule] = useState<number | null>(0); // First open by default
  const toggleModule = (index: number) => {
    setOpenModule(openModule === index ? null : index);
  };
  return <section className="py-16 md:py-24 px-4 bg-cream" data-id="element-209">
      <div className="max-w-3xl mx-auto" data-id="element-210">
        <FadeIn data-id="element-211">
          <h2 className="font-heading font-bold text-2xl md:text-4xl text-green-deep text-center mb-12" data-id="element-212">
            📋 QUÉ ENCONTRÁS ADENTRO
          </h2>
        </FadeIn>

        <div className="space-y-4" data-id="element-213">
          <FadeIn delay={0.1} data-id="element-214">
            <ModuleCard number="1" emoji="🟢" title="La Revolución Silenciosa" subtitle="Por qué la salud infantil empieza en el intestino, no en la farmacia" isOpen={openModule === 0} onToggle={() => toggleModule(0)} data-id="element-215">
              <p data-id="element-216">
                Vas a entender — en lenguaje de mamá, sin jerga médica —{' '}
                <strong data-id="element-217">por qué tu hijo se enferma</strong> y qué está pasando
                exactamente en su cuerpo cuando aparece el primer moco. Este
                conocimiento solo ya vale más que cualquier consulta de guardia.
              </p>
            </ModuleCard>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-218">
            <ModuleCard number="2" emoji="🟢" title="El Intestino como Comandante" subtitle="El 70% del sistema inmune vive acá" isOpen={openModule === 1} onToggle={() => toggleModule(1)} data-id="element-219">
              <p data-id="element-220">
                La tabla completa de <strong data-id="element-221">qué bacteria hace qué</strong> y
                qué alimento la nutre. Los 4 enemigos silenciosos de la
                microbiota infantil que probablemente ya están en tu cocina. Y
                la explicación del <strong data-id="element-222">Intestino Permeable</strong> — la
                causa raíz que nadie te diagnosticó pero que podría estar detrás
                de las alergias, el eczema y las infecciones repetidas de tu
                hijo.
              </p>
            </ModuleCard>
          </FadeIn>

          <FadeIn delay={0.3} data-id="element-223">
            <ModuleCard number="3" emoji="🔴" title="Protocolo SOS: Las Primeras 2 Horas" subtitle="La ventana que lo cambia todo" isOpen={openModule === 2} onToggle={() => toggleModule(2)} data-id="element-224">
              <p className="mb-4" data-id="element-225">
                Cuando el sistema inmune detecta un patógeno, tiene{' '}
                <strong data-id="element-226">2 a 4 horas</strong> para montar una respuesta efectiva.
                Si en ese tiempo recibe los nutrientes correctos, puede contener
                la infección antes de que se expanda.
              </p>
              <p className="mb-4" data-id="element-227">
                Este módulo te enseña a leer las{' '}
                <strong data-id="element-228">5 señales de alarma</strong> antes de que aparezca la
                fiebre — y actuar con precisión quirúrgica desde el primer
                minuto.
              </p>
              <div className="bg-cream p-4 rounded-xl text-sm border border-orange-warm/20" data-id="element-229">
                <p className="font-bold text-orange-warm mb-2" data-id="element-230">
                  ⏰ Protocolo hora a hora:
                </p>
                <ul className="space-y-2" data-id="element-231">
                  <li data-id="element-232">
                    <strong data-id="element-233">HORA 0</strong> → Identificar síntoma + eliminar
                    azúcar
                  </li>
                  <li data-id="element-234">
                    <strong data-id="element-235">HORA 1</strong> → Shot "Poción de Dragón" + cúrcuma
                  </li>
                  <li data-id="element-236">
                    <strong data-id="element-237">HORA 2</strong> → Evaluar fiebre + caldo de huesos
                  </li>
                  <li data-id="element-238">
                    <strong data-id="element-239">HORAS 4-24</strong> → Protocolo de mantenimiento
                  </li>
                </ul>
              </div>
            </ModuleCard>
          </FadeIn>

          <FadeIn delay={0.4} data-id="element-240">
            <ModuleCard number="4" emoji="🟢" title="Los 5 Pilares del Blindaje Intestinal" subtitle="El sistema completo" isOpen={openModule === 3} onToggle={() => toggleModule(3)} data-id="element-241">
              <div className="space-y-3" data-id="element-242">
                <div className="flex gap-3" data-id="element-243">
                  <span className="shrink-0" data-id="element-244">🌿</span>
                  <p data-id="element-245">
                    <strong data-id="element-246">Diversidad de Fibra:</strong> Alimenta 38 billones
                    de bacterias (Regla de las 30 plantas)
                  </p>
                </div>
                <div className="flex gap-3" data-id="element-247">
                  <span className="shrink-0" data-id="element-248">🫐</span>
                  <p data-id="element-249">
                    <strong data-id="element-250">Polifenoles:</strong> Bloquean adhesión viral
                    (Arándanos, granada, cacao)
                  </p>
                </div>
                <div className="flex gap-3" data-id="element-251">
                  <span className="shrink-0" data-id="element-252">🍲</span>
                  <p data-id="element-253">
                    <strong data-id="element-254">Caldo de Huesos:</strong> Sella el intestino
                    permeable (Protocolo semanal)
                  </p>
                </div>
                <div className="flex gap-3" data-id="element-255">
                  <span className="shrink-0" data-id="element-256">🥛</span>
                  <p data-id="element-257">
                    <strong data-id="element-258">Probióticos Reales:</strong> 50+ cepas vivas
                    (Introducción gradual)
                  </p>
                </div>
                <div className="flex gap-3" data-id="element-259">
                  <span className="shrink-0" data-id="element-260">☀️</span>
                  <p data-id="element-261">
                    <strong data-id="element-262">Vitamina D + Sol:</strong> El interruptor maestro
                    (15-20 min AM)
                  </p>
                </div>
              </div>
            </ModuleCard>
          </FadeIn>

          <FadeIn delay={0.5} data-id="element-263">
            <ModuleCard number="5" emoji="🟡" title="La Farmacia Natural de tu Alacena" subtitle="Los 10 antibióticos naturales más potentes" isOpen={openModule === 4} onToggle={() => toggleModule(4)} data-id="element-264">
              <p className="mb-3" data-id="element-265">
                <strong data-id="element-266">
                  El ajo en los nuggets. El jengibre en el smoothie. La cúrcuma
                  en el arroz.
                </strong>
              </p>
              <p data-id="element-267">
                Tu hijo va a consumir los antivirales más potentes de la
                naturaleza sin saber que los está tomando. Incluye{' '}
                <strong data-id="element-268">tabla completa</strong> de ingrediente, compuesto
                activo, acción y cómo darlo a niños sin rechazo.
              </p>
            </ModuleCard>
          </FadeIn>

          <FadeIn delay={0.6} data-id="element-269">
            <ModuleCard number="6" emoji="🍽️" title="Recetario: 15 Recetas Médicas" subtitle="Medicina disfrazada de comida deliciosa" isOpen={openModule === 5} onToggle={() => toggleModule(5)} data-id="element-270">
              <p data-id="element-271">
                Incluye Gomitas de Caldo, Sopa "Escudo Dorado", Smoothie
                Inmune-Pro, Nuggets con Zinc, Shot "Poción de Dragón" y más.
                Todo diseñado para que los niños lo pidan.
              </p>
            </ModuleCard>
          </FadeIn>

          <FadeIn delay={0.7} data-id="element-272">
            <ModuleCard number="7" emoji="🏫" title="Protocolo Jardín Maternal" subtitle="Los 3 momentos críticos del día" isOpen={openModule === 6} onToggle={() => toggleModule(6)} data-id="element-273">
              <p data-id="element-274">
                <strong data-id="element-275">
                  Antes de salir. Al volver a casa. La noche de reparación.
                </strong>{' '}
                El jardín es el principal punto de exposición. Este módulo
                define exactamente qué hacer en cada momento para que la
                exposición no derive en enfermedad.
              </p>
            </ModuleCard>
          </FadeIn>

          <FadeIn delay={0.8} data-id="element-276">
            <ModuleCard number="8" emoji="🗓️" title="El Calendario Inmunológico Anual" subtitle="Mes a mes, con anticipación" isOpen={openModule === 7} onToggle={() => toggleModule(7)} data-id="element-277">
              <p data-id="element-278">
                La inmunidad no se construye en una semana de invierno cuando ya
                hay síntomas. Se construye todo el año. Incluye{' '}
                <strong data-id="element-279">tabla mensual completa</strong> y sistema de seguimiento
                trimestral.
              </p>
            </ModuleCard>
          </FadeIn>

          <FadeIn delay={0.9} data-id="element-280">
            <ModuleCard number="9" emoji="💊" title="Suplementación Inteligente" subtitle="Cuándo la comida sola no alcanza" isOpen={openModule === 8} onToggle={() => toggleModule(8)} data-id="element-281">
              <p data-id="element-282">
                La guía honesta sobre qué suplementos tienen evidencia real en
                pediatría. Incluye el{' '}
                <strong data-id="element-283">Protocolo de Reconstrucción Post-Antibiótico</strong> —
                el más crítico y el más ignorado.
              </p>
            </ModuleCard>
          </FadeIn>
        </div>
      </div>
    </section>;
}